import { acceptCompletion, autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap, completionStatus, snippetCompletion, type Completion, type CompletionContext, type CompletionResult, type CompletionSource } from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { sql } from "@codemirror/lang-sql";
import { bracketMatching, defaultHighlightStyle, foldGutter, foldKeymap, foldService, indentOnInput, StreamLanguage, syntaxHighlighting, type StreamParser } from "@codemirror/language";
import { forEachDiagnostic, linter, nextDiagnostic, previousDiagnostic, type Diagnostic } from "@codemirror/lint";
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search";
import { EditorSelection, EditorState, Prec, StateEffect, StateField, type Extension, type Text } from "@codemirror/state";
import { drawSelection, dropCursor, EditorView, highlightActiveLine, highlightActiveLineGutter, keymap, lineNumbers, showTooltip, tooltips, ViewPlugin, type Tooltip, type ViewUpdate } from "@codemirror/view";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pluginSdkT, usePluginSdkT } from "../i18n";
import type { ResizableViewportHandlePosition } from "./ResizableViewport";
import { ResizableViewport } from "./ResizableViewport";

export type LspCallSidecar = (
    sidecarId: string,
    method: string,
    params?: Record<string, unknown>,
) => Promise<{ success: boolean; result?: unknown; error?: string }>;

export interface LspCodeEditorProps {
    value: string;
    onChange: (value: string) => void;
    languageId: "sql" | "typescript" | "javascript" | "tsx" | "jsx" | string;
    serverId?: string;
    uri: string;
    callSidecar?: LspCallSidecar | null;
    server?: {
        command?: string;
        args?: string[];
        rootUri?: string;
        workingDirectory?: string;
        configFile?: {
            fileName?: string;
            content?: unknown;
            contentText?: string;
        };
        initializationOptions?: unknown;
        settings?: unknown;
    };
    className?: string;
    height?: number;
    minHeight?: number;
    maxHeight?: number;
    placeholder?: string;
    readOnly?: boolean;
    lspEnabled?: boolean;
    onRunCommand?: () => void;
    onViewChange?: (view: EditorView | null) => void;
    completionSources?: CompletionSource[];
    hoverSource?: LspHoverSource;
    extraExtensions?: Extension[];
    showStatus?: boolean;
    statusLabel?: string;
    lineWrapping?: boolean;
    fillAvailable?: boolean;
    resizable?: boolean;
    resizeStorageKey?: string;
    resizeHandlePosition?: ResizableViewportHandlePosition;
    onHeightChange?: (height: number) => void;
    onHeightCommit?: (height: number) => void;
}

export interface LspHoverContext {
    token: string;
    range: { from: number; to: number };
    lineText: string;
    pos: number;
    languageId: string;
}

export type LspHoverSource = (context: LspHoverContext) => string | null | Promise<string | null>;

export type LspConnectionStatus = "off" | "local" | "connecting" | "ready" | "error";
type PendingDocumentOpen = { key: string; promise: Promise<void> };

type LspConfig = {
    serverId: string;
    uri: string;
    languageId: string;
    command?: string;
    args?: string[];
    rootUri?: string;
    workingDirectory?: string;
    configFile?: {
        fileName?: string;
        content?: unknown;
        contentText?: string;
    };
    initializationOptions?: unknown;
    settings?: unknown;
    callSidecar?: LspCallSidecar | null;
};

export type LspEditorSelectionRange = {
    anchor: number;
    head: number;
};

export type LspEditorSelectionSnapshot = LspEditorSelectionRange & {
    documentKey: string;
};

type LspCompletionItem = {
    label?: string;
    kind?: number;
    detail?: string;
    documentation?: unknown;
    insertText?: string;
    insertTextFormat?: number;
    textEdit?: { newText?: string };
};

type LspDiagnostic = {
    range?: {
        start?: { line?: number; character?: number };
        end?: { line?: number; character?: number };
    };
    severity?: number;
    message?: string;
    source?: string;
};

type LspPosition = {
    line?: number;
    character?: number;
};

type LspRange = {
    start?: LspPosition;
    end?: LspPosition;
};

export type LspTextEdit = {
    range?: LspRange;
    newText?: string;
};

type LspWorkspaceTextDocumentEdit = {
    textDocument?: {
        uri?: string;
    };
    edits?: LspTextEdit[];
};

type LspWorkspaceEdit = {
    changes?: Record<string, LspTextEdit[]>;
    documentChanges?: Array<LspWorkspaceTextDocumentEdit | Record<string, unknown>>;
};

type LspCodeAction = {
    title?: string;
    edit?: LspWorkspaceEdit;
    data?: unknown;
    diagnostics?: unknown[];
    isPreferred?: boolean;
    kind?: string;
};

type LspSignatureParameter = {
    label?: string | [number, number];
    documentation?: unknown;
};

type LspSignatureInformation = {
    label?: string;
    documentation?: unknown;
    parameters?: LspSignatureParameter[];
    activeParameter?: number;
};

type LspSignatureHelp = {
    signatures?: LspSignatureInformation[];
    activeSignature?: number;
    activeParameter?: number;
};

export type LspSignatureViewModel = {
    label: string;
    documentation?: string;
    parameterLabel?: string;
    parameterDocumentation?: string;
    parameterSpan?: [number, number];
};

export type LspDiagnosticSummary = {
    errors: number;
    warnings: number;
    infos: number;
    total: number;
};

const lspDocumentGenerations = new Map<string, number>();
const lspDocumentCloseTimers = new Map<string, ReturnType<typeof setTimeout>>();
const EMPTY_COMPLETION_SOURCES: CompletionSource[] = [];
const EMPTY_EDITOR_EXTENSIONS: Extension[] = [];
const EMPTY_DIAGNOSTIC_SUMMARY: LspDiagnosticSummary = {
    errors: 0,
    warnings: 0,
    infos: 0,
    total: 0,
};
const CODE_MIRROR_SELECTION_BACKGROUND =
    "var(--editor-selection-background, color-mix(in srgb, var(--color-info, #0ea5e9) 24%, transparent))";

const setLspHoverTooltip = StateEffect.define<Tooltip | null>();
const setLspSignatureTooltip = StateEffect.define<Tooltip | null>();

const lspHoverTooltipField = StateField.define<Tooltip | null>({
    create: () => null,
    update(value, transaction) {
        if (transaction.docChanged || transaction.selection) value = null;
        for (const effect of transaction.effects) {
            if (effect.is(setLspHoverTooltip)) return effect.value;
        }
        return value;
    },
    provide: (field) => showTooltip.from(field),
});

const lspSignatureTooltipField = StateField.define<Tooltip | null>({
    create: () => null,
    update(value, transaction) {
        if (transaction.docChanged && !transaction.effects.some((effect) => effect.is(setLspSignatureTooltip))) {
            value = null;
        }
        for (const effect of transaction.effects) {
            if (effect.is(setLspSignatureTooltip)) return effect.value;
        }
        return value;
    },
    provide: (field) => showTooltip.from(field),
});

const completionKindMap: Record<number, string> = {
    1: "text",
    2: "function",
    3: "function",
    4: "class",
    5: "class",
    6: "variable",
    9: "module",
    10: "property",
    13: "keyword",
    17: "keyword",
    19: "file",
    21: "constant",
};

const lspCompletionKeymap = [
    {
        key: "Enter",
        run: (view: EditorView) => {
            if (!shouldAcceptCompletionOnEnter(completionStatus(view.state))) return false;
            return acceptCompletion(view);
        },
    },
    ...completionKeymap.filter((binding) => binding.key !== "Enter"),
];

export function shouldAcceptCompletionOnEnter(status: string | null): boolean {
    return status === "active" || status === "pending";
}

export function shouldUseResizableCodeEditor(
    resizable: boolean | undefined,
    height: number | undefined,
    maxHeight: number | undefined,
    fillAvailable = false,
): boolean {
    if (fillAvailable) return true;
    if (resizable !== undefined) return resizable;
    return !(typeof height === "number" && typeof maxHeight === "number" && maxHeight <= height);
}

export function resizableCodeEditorDefaultHeight(
    height: number | undefined,
    minHeight: number,
    maxHeight: number | undefined,
): number {
    const base = height ?? Math.max(minHeight, 220);
    const minClamped = Math.max(minHeight, base);
    return typeof maxHeight === "number" ? Math.min(maxHeight, minClamped) : minClamped;
}

export function codeEditorResizeStorageKey(languageId: string, uri: string): string {
    return `code-editor:${languageId}:${uri}`;
}

const lspTooltipTheme = EditorView.baseTheme({
    ".cm-tooltip.cm-lsp-completion-tooltip, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip": {
        zIndex: "var(--z-popover, 1000)",
        overflow: "hidden",
        pointerEvents: "auto",
        border: "1px solid var(--color-border, var(--border))",
        borderRadius: "8px",
        backgroundColor: "var(--color-popover, var(--popover))",
        color: "var(--color-popover-foreground, var(--popover-foreground))",
        boxShadow: "0 12px 34px rgb(0 0 0 / 0.16)",
        padding: "4px",
        fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
        fontSize: "12px",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip > ul, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip > ul": {
        maxHeight: "260px",
        minWidth: "240px",
        padding: "0",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip li, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip li": {
        minHeight: "26px",
        borderRadius: "6px",
        padding: "3px 8px",
        display: "flex",
        alignItems: "center",
        gap: "7px",
        cursor: "pointer",
        color: "var(--color-popover-foreground, var(--popover-foreground))",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip li[aria-selected], .cm-tooltip-autocomplete.cm-lsp-completion-tooltip li[aria-selected]": {
        backgroundColor: "color-mix(in srgb, var(--color-accent, var(--accent)) 78%, transparent)",
        color: "var(--color-accent-foreground, var(--accent-foreground))",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip .cm-completionIcon, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip .cm-completionIcon": {
        width: "16px",
        height: "16px",
        marginRight: "0",
        opacity: "0.76",
        color: "var(--color-muted-foreground, var(--muted-foreground))",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip .cm-completionLabel, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip .cm-completionLabel": {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "12px",
        lineHeight: "18px",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip .cm-completionMatchedText, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip .cm-completionMatchedText": {
        textDecoration: "none",
        fontWeight: "700",
        color: "var(--syntax-keyword, currentColor)",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip .cm-completionDetail, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip .cm-completionDetail": {
        marginLeft: "auto",
        color: "var(--color-muted-foreground, var(--muted-foreground))",
        fontSize: "10px",
        fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
        opacity: "0.82",
    },
    ".cm-tooltip.cm-lsp-completion-tooltip .cm-completionInfo, .cm-tooltip-autocomplete.cm-lsp-completion-tooltip .cm-completionInfo": {
        border: "1px solid var(--color-border, var(--border))",
        borderRadius: "8px",
        backgroundColor: "var(--color-popover, var(--popover))",
        color: "var(--color-popover-foreground, var(--popover-foreground))",
        boxShadow: "0 12px 34px rgb(0 0 0 / 0.16)",
        padding: "8px 10px",
        maxWidth: "360px",
        fontSize: "12px",
        lineHeight: "1.45",
    },
    ".cm-tooltip.cm-tooltip-hover": {
        zIndex: "var(--z-popover, 1000)",
        overflow: "hidden",
        pointerEvents: "auto",
        border: "1px solid var(--color-border, var(--border))",
        borderRadius: "8px",
        backgroundColor: "var(--color-popover, var(--popover))",
        color: "var(--color-popover-foreground, var(--popover-foreground))",
        boxShadow: "0 12px 34px rgb(0 0 0 / 0.16)",
        padding: "0",
    },
    ".cm-tooltip.cm-tooltip-hover.cm-lsp-hover, .cm-tooltip-hover.cm-lsp-hover": {
        overflow: "hidden",
        maxHeight: "min(320px, calc(100vh - 16px))",
    },
    ".cm-lsp-hover": {
        maxWidth: "480px",
        maxHeight: "320px",
        minHeight: "0",
        overflow: "hidden",
        pointerEvents: "auto",
        display: "flex",
        flexDirection: "column",
        padding: "0",
        fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
        fontSize: "12px",
        lineHeight: "1.45",
        color: "var(--color-popover-foreground, var(--popover-foreground))",
    },
    ".cm-lsp-hover .mh-title": {
        flex: "0 0 auto",
        margin: "0",
        padding: "7px 10px",
        borderBottom: "1px solid var(--color-border, var(--border))",
        background: "color-mix(in srgb, var(--color-muted, var(--muted)) 55%, transparent)",
        fontWeight: "600",
    },
    ".cm-lsp-hover .mh-body": {
        flex: "1 1 auto",
        minHeight: "0",
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        padding: "0 0 10px",
    },
    ".cm-lsp-hover .mh-line": {
        margin: "2px 0",
        padding: "0 10px",
        color: "var(--color-muted-foreground, var(--muted-foreground))",
        fontSize: "11px",
    },
    ".cm-lsp-hover .mh-sep": {
        border: "none",
        borderTop: "1px solid var(--color-border, var(--border))",
        margin: "8px 10px",
    },
    ".cm-lsp-hover b": {
        color: "var(--color-popover-foreground, var(--popover-foreground))",
        fontWeight: "650",
    },
    ".cm-lsp-hover i": {
        color: "var(--color-muted-foreground, var(--muted-foreground))",
        fontStyle: "normal",
    },
    ".cm-lsp-hover code": {
        borderRadius: "4px",
        background: "color-mix(in srgb, var(--color-muted, var(--muted)) 65%, transparent)",
        color: "var(--syntax-code, var(--color-foreground, var(--foreground)))",
        padding: "1px 4px",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "11px",
    },
    ".cm-lsp-hover .mh-code": {
        margin: "6px 10px 2px",
        overflow: "visible",
        borderRadius: "6px",
        background: "var(--color-muted, var(--muted))",
        color: "var(--color-foreground, var(--foreground))",
        padding: "7px 8px",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "11px",
        lineHeight: "1.5",
    },
    ".cm-lsp-hover .mh-code code": {
        background: "transparent",
        color: "inherit",
        padding: "0",
        fontSize: "inherit",
    },
    ".cm-lsp-hover .mh-list": {
        margin: "3px 0",
        padding: "0 10px",
        listStyle: "none",
    },
    ".cm-lsp-hover .mh-list li": {
        position: "relative",
        margin: "1px 0",
        paddingLeft: "12px",
        color: "var(--color-muted-foreground, var(--muted-foreground))",
        fontSize: "11px",
    },
    ".cm-lsp-hover .mh-list li::before": {
        content: "'·'",
        position: "absolute",
        left: "2px",
        fontWeight: "700",
        color: "var(--syntax-keyword, currentColor)",
    },
    ".cm-lsp-hover .mh-table-wrap": {
        margin: "6px 10px 2px",
        maxWidth: "calc(100% - 20px)",
        overflow: "visible",
        border: "1px solid var(--color-border, var(--border))",
        borderRadius: "6px",
    },
    ".cm-lsp-hover .mh-table": {
        width: "100%",
        tableLayout: "fixed",
        borderCollapse: "collapse",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "11px",
        lineHeight: "1.45",
    },
    ".cm-lsp-hover .mh-table th, .cm-lsp-hover .mh-table td": {
        padding: "4px 7px",
        borderBottom: "1px solid var(--color-border, var(--border))",
        textAlign: "left",
        verticalAlign: "top",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
    },
    ".cm-lsp-hover .mh-table th": {
        background: "color-mix(in srgb, var(--color-muted, var(--muted)) 55%, transparent)",
        color: "var(--color-popover-foreground, var(--popover-foreground))",
        fontWeight: "650",
    },
    ".cm-lsp-hover .mh-table td": {
        color: "var(--color-muted-foreground, var(--muted-foreground))",
    },
    ".cm-lsp-hover .mh-table tbody tr:last-child td": {
        borderBottom: "0",
    },
    ".cm-lsp-hover .mh-table .mh-align-center": {
        textAlign: "center",
    },
    ".cm-lsp-hover .mh-table .mh-align-right": {
        textAlign: "right",
    },
    ".cm-lsp-signature": {
        maxWidth: "560px",
        overflow: "hidden",
        padding: "0",
        fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
        fontSize: "12px",
        lineHeight: "1.45",
        color: "var(--color-popover-foreground, var(--popover-foreground))",
    },
    ".cm-lsp-signature .mh-title": {
        padding: "7px 10px",
        borderBottom: "1px solid var(--color-border, var(--border))",
        background: "color-mix(in srgb, var(--color-muted, var(--muted)) 55%, transparent)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "11px",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
    ".cm-lsp-signature .mh-active-param": {
        borderRadius: "4px",
        background: "color-mix(in srgb, var(--color-primary, var(--primary)) 14%, transparent)",
        color: "var(--color-foreground, var(--foreground))",
        fontWeight: "700",
    },
    ".cm-lsp-signature .mh-body": {
        padding: "7px 10px 8px",
        maxHeight: "220px",
        overflowY: "auto",
    },
    ".cm-lsp-signature .mh-line": {
        padding: "0",
        color: "var(--color-muted-foreground, var(--muted-foreground))",
        fontSize: "11px",
    },
});

const mermaidKeywords = [
    "flowchart",
    "graph",
    "sequenceDiagram",
    "classDiagram",
    "stateDiagram",
    "stateDiagram-v2",
    "erDiagram",
    "gantt",
    "pie",
    "journey",
    "mindmap",
    "timeline",
    "gitGraph",
    "quadrantChart",
    "xychart-beta",
    "participant",
    "actor",
    "activate",
    "deactivate",
    "loop",
    "alt",
    "else",
    "opt",
    "par",
    "and",
    "critical",
    "option",
    "break",
    "rect",
    "end",
    "Note",
    "note",
    "over",
    "right",
    "left",
    "of",
    "class",
    "interface",
    "namespace",
    "state",
    "section",
    "title",
    "dateFormat",
    "axisFormat",
    "excludes",
    "todayMarker",
    "accTitle",
    "accDescr",
    "direction",
] as const;

const mermaidKeywordPattern = new RegExp(
    `^(?:${mermaidKeywords.map(escapeRegExp).join("|")})\\b`,
);

const mermaidStreamParser: StreamParser<Record<string, never>> = {
    name: "mermaid",
    token(stream) {
        if (stream.match(/^%%.*/)) return "comment";
        if (stream.match(/^\s+/)) return null;
        if (stream.match(/^"(?:[^"\\]|\\.)*"/)) return "string";
        if (stream.match(/^\|[^|]*\|/)) return "string";
        if (stream.match(/^(?:<-->|-->|---|==>|-.->|--x|--o|<--|-\.-|===|:::)/)) return "operator";
        if (stream.match(/^(?:\[[^\]]*\]|\([^)]*\)|\{[^}]*\})/)) return "string";
        if (stream.match(mermaidKeywordPattern)) return "keyword";
        if (stream.match(/^(?:TD|TB|BT|LR|RL)\b/)) return "atom";
        if (stream.match(/^\d+(?:\.\d+)?\b/)) return "number";
        if (stream.match(/^[A-Za-z_][\w-]*/)) return "variableName";
        stream.next();
        return null;
    },
    languageData: {
        commentTokens: { line: "%%" },
        closeBrackets: { brackets: ["(", "[", "{", "\""] },
    },
};

function mermaidLanguageExtension(): Extension {
    return [
        StreamLanguage.define(mermaidStreamParser),
        foldService.of((state, lineStart) => {
            const line = state.doc.lineAt(lineStart);
            return mermaidFoldRange(state.doc, line.number);
        }),
    ];
}

export function shouldPropagateLspEditorChange(
    nextValue: string,
    currentValue: string,
    syncingExternalValue: boolean,
): boolean {
    return !syncingExternalValue && nextValue !== currentValue;
}

export function shouldRequestHover(
    callSidecar: LspCallSidecar | null | undefined,
    hoverSource: LspHoverSource | null | undefined,
): boolean {
    return !!callSidecar || !!hoverSource;
}

export function lspEditorDocumentKey(languageId: string, uri: string): string {
    return `${languageId}\u0000${uri}`;
}

export function clampLspEditorSelection(
    selection: LspEditorSelectionRange,
    documentLength: number,
): LspEditorSelectionRange {
    const max = Math.max(0, Math.trunc(documentLength));
    return {
        anchor: clampLspEditorSelectionOffset(selection.anchor, max),
        head: clampLspEditorSelectionOffset(selection.head, max),
    };
}

export function createLspEditorSelectionSnapshot(
    documentKey: string,
    selection: LspEditorSelectionRange,
): LspEditorSelectionSnapshot {
    return {
        documentKey,
        anchor: selection.anchor,
        head: selection.head,
    };
}

export function lspEditorSelectionForDocument(
    snapshot: LspEditorSelectionSnapshot | null | undefined,
    documentKey: string,
    documentLength: number,
): LspEditorSelectionRange | null {
    if (!snapshot || snapshot.documentKey !== documentKey) return null;
    return clampLspEditorSelection(snapshot, documentLength);
}

export function lspDiagnosticSummaryFromDiagnostics(
    diagnostics: readonly Pick<Diagnostic, "severity">[],
): LspDiagnosticSummary {
    const summary = { ...EMPTY_DIAGNOSTIC_SUMMARY };
    for (const diagnostic of diagnostics) {
        summary.total += 1;
        if (diagnostic.severity === "error") {
            summary.errors += 1;
        } else if (diagnostic.severity === "warning") {
            summary.warnings += 1;
        } else {
            summary.infos += 1;
        }
    }
    return summary;
}

export function formatLspDiagnosticSummary(summary: LspDiagnosticSummary): string {
    const parts = [
        summary.errors ? `E${summary.errors}` : "",
        summary.warnings ? `W${summary.warnings}` : "",
        summary.infos ? `I${summary.infos}` : "",
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : "OK";
}

function clampLspEditorSelectionOffset(offset: number, max: number): number {
    if (!Number.isFinite(offset)) return 0;
    return Math.min(Math.max(Math.trunc(offset), 0), max);
}

export function lspCompletionFrom(lineText: string, lineFrom: number, pos: number): number {
    const localPos = Math.min(Math.max(pos - lineFrom, 0), lineText.length);
    const before = lineText.slice(0, localPos);
    const segment = before.match(/[\w$]*$/)?.[0] ?? "";
    return lineFrom + localPos - segment.length;
}

export function LspCodeEditor({
    value,
    onChange,
    languageId,
    serverId,
    uri,
    callSidecar,
    server,
    className,
    height,
    minHeight = 96,
    maxHeight,
    placeholder,
    readOnly = false,
    lspEnabled = true,
    onRunCommand,
    onViewChange,
    completionSources = EMPTY_COMPLETION_SOURCES,
    hoverSource,
    extraExtensions = EMPTY_EDITOR_EXTENSIONS,
    showStatus = true,
    statusLabel = "LSP",
    lineWrapping = true,
    fillAvailable = false,
    resizable,
    resizeStorageKey,
    resizeHandlePosition = "bottom",
    onHeightChange,
    onHeightCommit,
}: LspCodeEditorProps) {
    const t = usePluginSdkT();
    const hostRef = useRef<HTMLDivElement | null>(null);
    const viewRef = useRef<EditorView | null>(null);
    const onChangeRef = useRef(onChange);
    const valueRef = useRef(value);
    const syncingExternalValueRef = useRef(false);
    const selectionSnapshotRef = useRef<LspEditorSelectionSnapshot | null>(null);
    const versionRef = useRef(1);
    const changeTimerRef = useRef<number | null>(null);
    const runCommandRef = useRef(onRunCommand);
    const onViewChangeRef = useRef(onViewChange);
    const pendingDocumentOpenRef = useRef<PendingDocumentOpen | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<LspConnectionStatus>(
        lspEnabled && callSidecar ? "connecting" : "local",
    );

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        runCommandRef.current = onRunCommand;
    }, [onRunCommand]);

    useEffect(() => {
        onViewChangeRef.current = onViewChange;
    }, [onViewChange]);

    const argsKey = JSON.stringify(server?.args ?? []);
    const configFileKey = JSON.stringify(server?.configFile ?? null);
    const initializationOptionsKey = JSON.stringify(server?.initializationOptions ?? null);
    const settingsKey = JSON.stringify(server?.settings ?? null);
    const stableCallSidecar = useStableLspCallSidecar(callSidecar);
    const useResizableViewport = shouldUseResizableCodeEditor(resizable, height, maxHeight, fillAvailable);
    const documentKey = lspEditorDocumentKey(languageId, uri);

    useEffect(() => {
        valueRef.current = value;
        const view = viewRef.current;
        if (!view) return;
        const current = view.state.doc.toString();
        if (current !== value) {
            const nextSelection = clampLspEditorSelection(view.state.selection.main, value.length);
            selectionSnapshotRef.current = createLspEditorSelectionSnapshot(documentKey, nextSelection);
            syncingExternalValueRef.current = true;
            try {
                view.dispatch({
                    changes: { from: 0, to: current.length, insert: value },
                    selection: EditorSelection.single(nextSelection.anchor, nextSelection.head),
                });
            } finally {
                syncingExternalValueRef.current = false;
            }
        }
    }, [documentKey, value]);

    const config = useMemo<LspConfig>(() => ({
        serverId: serverId ?? defaultServerIdForLanguage(languageId),
        uri,
        languageId,
        command: server?.command,
        args: server?.args,
        rootUri: server?.rootUri,
        workingDirectory: server?.workingDirectory,
        configFile: server?.configFile,
        initializationOptions: server?.initializationOptions,
        settings: server?.settings,
        callSidecar: lspEnabled ? stableCallSidecar : null,
    }), [
        argsKey,
        configFileKey,
        initializationOptionsKey,
        languageId,
        lspEnabled,
        server?.command,
        server?.rootUri,
        server?.workingDirectory,
        serverId,
        settingsKey,
        stableCallSidecar,
        uri,
    ]);

    const languageExtension = useMemo((): Extension => {
        if (languageId === "json") return json();
        if (languageId === "mermaid") return mermaidLanguageExtension();
        if (languageId === "sql") return sql();
        if (languageId === "typescript" || languageId === "tsx" || languageId === "typescriptreact") {
            return javascript({ typescript: true, jsx: languageId === "tsx" || languageId === "typescriptreact" });
        }
        if (languageId === "javascript" || languageId === "jsx" || languageId === "javascriptreact") {
            return javascript({ jsx: languageId === "jsx" || languageId === "javascriptreact" });
        }
        return [];
    }, [languageId]);

    const nextDocumentVersion = useCallback(() => {
        versionRef.current += 1;
        return versionRef.current;
    }, []);

    const waitForDocumentOpen = useCallback(async (targetConfig: LspConfig) => {
        const pending = pendingDocumentOpenRef.current;
        if (!pending || !shouldAwaitPendingLspOpen(pending.key, lspDocumentKey(targetConfig))) {
            return;
        }
        await pending.promise;
    }, []);

    const sendChange = useCallback((text: string) => {
        if (changeTimerRef.current !== null) window.clearTimeout(changeTimerRef.current);
        changeTimerRef.current = window.setTimeout(() => {
            changeTimerRef.current = null;
            if (!config.callSidecar) return;
            waitForDocumentOpen(config)
                .then(() => {
                    if (!config.callSidecar) return null;
                    const version = nextDocumentVersion();
                    return callLsp(config, "lsp/document.change", {
                        serverId: config.serverId,
                        uri: config.uri,
                        text,
                        version,
                    });
                })
                .then(() => {
                    setError(null);
                    setStatus("ready");
                })
                .catch((err: unknown) => {
                    setError((err as Error).message);
                    setStatus("error");
                });
        }, 250);
    }, [config, nextDocumentVersion, waitForDocumentOpen]);

    useEffect(() => {
        if (!hostRef.current) return;
        let cancelled = false;

        const updateListener = EditorView.updateListener.of((update) => {
            if (update.selectionSet || update.docChanged) {
                selectionSnapshotRef.current = createLspEditorSelectionSnapshot(
                    documentKey,
                    update.state.selection.main,
                );
            }
            if (!update.docChanged) return;
            const next = update.state.doc.toString();
            if (!shouldPropagateLspEditorChange(
                next,
                valueRef.current,
                syncingExternalValueRef.current,
            )) return;
            valueRef.current = next;
            onChangeRef.current(next);
            sendChange(next);
        });
        const runKeymap = onRunCommand
            ? keymap.of([{
                key: "Mod-Enter",
                run: () => {
                    runCommandRef.current?.();
                    return true;
                },
            }])
            : [];
        const hasFixedHeight = useResizableViewport || typeof height === "number";
        const tooltipParent = hostRef.current.ownerDocument.body;
        const theme = EditorView.theme({
            "&": {
                width: "100%",
                maxWidth: "100%",
                minWidth: "0",
                boxSizing: "border-box",
                minHeight: `${minHeight}px`,
                ...(hasFixedHeight ? { height: "100%" } : {}),
                ...(maxHeight && !hasFixedHeight ? { maxHeight: `${maxHeight}px` } : {}),
                border: "1px solid var(--border)",
                borderRadius: "6px",
                backgroundColor: "var(--background)",
                fontSize: "12px",
                lineHeight: "1.55",
                overflow: "hidden",
                position: "relative",
            },
            ".cm-gutters": {
                backgroundColor: "transparent",
                borderRight: "1px solid var(--border)",
                color: "var(--muted-foreground)",
            },
            ".cm-gutterElement": {
                lineHeight: "18px",
                minHeight: "18px",
            },
            ".cm-lineNumbers .cm-gutterElement": {
                minWidth: "24px",
                padding: "0 6px 0 4px",
            },
            ".cm-foldGutter .cm-gutterElement": {
                minWidth: "14px",
                padding: "0 2px",
                textAlign: "center",
            },
            ".cm-activeLineGutter": {
                backgroundColor: "var(--muted)",
            },
            "&.cm-focused .cm-selectionBackground": {
                backgroundColor: CODE_MIRROR_SELECTION_BACKGROUND,
            },
            ".cm-selectionBackground": {
                backgroundColor: CODE_MIRROR_SELECTION_BACKGROUND,
            },
            ".cm-content ::selection": {
                backgroundColor: CODE_MIRROR_SELECTION_BACKGROUND,
                color: "inherit",
            },
            ".cm-scroller": {
                overflowX: lineWrapping ? "hidden" : "auto",
                overflowY: "auto",
                ...(hasFixedHeight ? { height: "100%" } : {}),
                ...(maxHeight && !hasFixedHeight ? { maxHeight: `${maxHeight}px` } : {}),
                minWidth: "0",
                maxWidth: "100%",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            },
            ".cm-content": {
                padding: "2px 0",
                minWidth: "0",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                ...(lineWrapping
                    ? {
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                    }
                    : {}),
            },
            ".cm-line": {
                minHeight: "18px",
                padding: "0 8px",
                overflowWrap: "anywhere",
                ...(lineWrapping
                    ? {
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }
                    : {}),
            },
            ".cm-placeholder": { color: "var(--muted-foreground)" },
        });
        const initialSelection = lspEditorSelectionForDocument(
            selectionSnapshotRef.current,
            documentKey,
            valueRef.current.length,
        );

        const state = EditorState.create({
            doc: valueRef.current,
            selection: initialSelection
                ? EditorSelection.single(initialSelection.anchor, initialSelection.head)
                : undefined,
            extensions: [
                lineNumbers(),
                foldGutter({
                    openText: "⌄",
                    closedText: "›",
                }),
                highlightActiveLineGutter(),
                history(),
                dropCursor(),
                indentOnInput(),
                bracketMatching(),
                closeBrackets(),
                drawSelection(),
                highlightActiveLine(),
                highlightSelectionMatches(),
                languageExtension,
                syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
                lineWrapping ? EditorView.lineWrapping : [],
                ...lspExtensions(
                    config,
                    lspEnabled,
                    nextDocumentVersion,
                    waitForDocumentOpen,
                    tooltipParent,
                    completionSources,
                    hoverSource,
                ),
                lspTooltipTheme,
                EditorView.editable.of(!readOnly),
                ...extraExtensions,
                Prec.highest(keymap.of(lspCompletionKeymap)),
                keymap.of([
                    ...closeBracketsKeymap,
                    ...defaultKeymap,
                    ...foldKeymap,
                    ...searchKeymap,
                    ...historyKeymap,
                    indentWithTab,
                ]),
                runKeymap,
                updateListener,
                theme,
            ],
        });
        const view = new EditorView({ state, parent: hostRef.current });
        viewRef.current = view;
        onViewChangeRef.current?.(view);

        if (config.callSidecar) {
            setStatus("connecting");
            const openKey = lspDocumentKey(config);
            const openPromise = openDocument(config, valueRef.current, nextDocumentVersion(), () => cancelled)
                .then(() => {
                    if (cancelled) return;
                    setError(null);
                    setStatus("ready");
                })
                .catch((err: unknown) => {
                    if (cancelled) return;
                    setError((err as Error).message);
                    setStatus("error");
                    throw err;
                })
                .finally(() => {
                    if (pendingDocumentOpenRef.current?.key === openKey) {
                        pendingDocumentOpenRef.current = null;
                    }
                });
            pendingDocumentOpenRef.current = { key: openKey, promise: openPromise };
            void openPromise.catch(() => {});
        } else {
            pendingDocumentOpenRef.current = null;
            setStatus(lspEnabled ? "local" : "off");
        }

        return () => {
            cancelled = true;
            if (changeTimerRef.current !== null) window.clearTimeout(changeTimerRef.current);
            if (config.callSidecar) {
                scheduleLspDocumentClose(config);
            }
            selectionSnapshotRef.current = createLspEditorSelectionSnapshot(
                documentKey,
                view.state.selection.main,
            );
            view.destroy();
            if (viewRef.current === view) viewRef.current = null;
            onViewChangeRef.current?.(null);
        };
    }, [completionSources, config, documentKey, extraExtensions, fillAvailable, height, hoverSource, languageExtension, lineWrapping, lspEnabled, maxHeight, minHeight, nextDocumentVersion, readOnly, sendChange, useResizableViewport, waitForDocumentOpen, !!onRunCommand]);

    const statusView = lspStatusView(lspEnabled ? status : "off", error, statusLabel, t);

    const editorHostStyle = useResizableViewport
        ? { height: "100%", minHeight: 0, maxHeight: "none" }
        : height
            ? { height: `${height}px`, minHeight: `${minHeight}px`, maxHeight: `${height}px` }
            : maxHeight
                ? { minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px` }
                : undefined;
    const editorHost = (
        <div
            ref={hostRef}
            aria-label={placeholder}
            className={
                useResizableViewport || height || maxHeight
                    ? "min-h-0 min-w-0 max-w-full overflow-hidden"
                    : undefined
            }
            style={editorHostStyle}
        />
    );

    return (
        <div
            className={[
                "min-w-0 max-w-full overflow-hidden",
                fillAvailable ? "flex h-full min-h-0 flex-col" : "",
                className,
            ].filter(Boolean).join(" ")}
        >
            {useResizableViewport ? (
                <ResizableViewport
                    fillAvailable={fillAvailable}
                    defaultHeight={resizableCodeEditorDefaultHeight(height, minHeight, maxHeight)}
                    min={minHeight}
                    max={maxHeight}
                    height={onHeightChange ? height : undefined}
                    onHeightChange={onHeightChange}
                    onHeightCommit={onHeightCommit}
                    storageKey={
                        resizeStorageKey ?? codeEditorResizeStorageKey(languageId, uri)
                    }
                    handlePosition={resizeHandlePosition}
                    contentClassName="min-h-0 min-w-0 overflow-hidden"
                    title={t("lsp.resizeEditor")}
                >
                    {editorHost}
                </ResizableViewport>
            ) : editorHost}
            {showStatus && (
                <div className="mt-1 flex items-center justify-end gap-2">
                    <span
                        className={`inline-flex max-w-full items-center gap-1.5 truncate text-[10px] ${statusView.className}`}
                        title={statusView.title}
                    >
                        <span className={`h-1.5 w-1.5 rounded-full ${statusView.dotClassName}`} />
                        <span className="truncate">{statusView.label}</span>
                    </span>
                </div>
            )}
        </div>
    );
}

export function useStableLspCallSidecar(callSidecar: LspCallSidecar | null | undefined): LspCallSidecar | null {
    const ref = useRef(callSidecar);
    useEffect(() => {
        ref.current = callSidecar;
    }, [callSidecar]);
    const stableCall = useCallback<LspCallSidecar>(async (sidecarId, method, params) => {
        const current = ref.current;
        if (!current) {
            return { success: false, error: "LSP sidecar is unavailable" };
        }
        return current(sidecarId, method, params);
    }, []);
    return callSidecar ? stableCall : null;
}

function lspStatusView(
    status: LspConnectionStatus,
    error: string | null,
    label: string,
    t: (key: string, params?: Record<string, unknown>) => string,
) {
    if (status === "ready") {
        return {
            label: t("lsp.status.connected", { label }),
            title: t("lsp.status.connected", { label }),
            className: "plugin-tone-success-text",
            dotClassName: "bg-current",
        };
    }
    if (status === "connecting") {
        return {
            label: t("lsp.status.connecting", { label }),
            title: t("lsp.status.connecting", { label }),
            className: "text-muted-foreground",
            dotClassName: "bg-current",
        };
    }
    if (status === "error") {
        const message = friendlyLspError(error);
        return {
            label: message
                ? `${label}: ${message}`
                : t("lsp.status.error", { label }),
            title: error
                ? t("lsp.status.startFailed", { label, error })
                : t("lsp.status.error", { label }),
            className: "plugin-tone-warning-text",
            dotClassName: "bg-current",
        };
    }
    if (status === "off") {
        return {
            label: t("lsp.status.off"),
            title: t("lsp.status.off"),
            className: "text-muted-foreground",
            dotClassName: "bg-current",
        };
    }
    return {
        label: t("lsp.status.disconnected"),
        title: t("lsp.status.disconnected"),
        className: "text-muted-foreground",
        dotClassName: "bg-current",
    };
}

export function friendlyLspError(error: string | null): string | null {
    if (!error) return null;
    const cleaned = error.replace(/^\[-?\d+\]\s*/, "").trim();
    if (/no such module:\s*fts5/i.test(cleaned)) {
        return pluginSdkT("lsp.error.sqlsMissingFts5");
    }
    const panicCommand = cleaned.match(/LSP server '([^']+)'.*\bpanic\(/i)?.[1];
    if (panicCommand) {
        return pluginSdkT("lsp.error.serverCrashed", { command: panicCommand });
    }
    if (/\bpanic\(/i.test(cleaned)) {
        return pluginSdkT("lsp.error.crashed");
    }
    const missingCommand = cleaned.match(/failed to start ([^:]+):/i)?.[1];
    if (missingCommand) {
        return pluginSdkT("lsp.error.commandMissing", { command: missingCommand });
    }
    const exited = cleaned.match(/LSP server '([^']+)' exited[^:]*:?\s*(.*?)(?:;\s*write LSP frame:.*)?$/i);
    if (exited) {
        const command = exited[1];
        const reason = exited[2]?.trim().replace(/^\d+:\s*/, "");
        return reason
            ? pluginSdkT("lsp.error.serverExitedWithReason", { command, reason })
            : pluginSdkT("lsp.error.serverExited", { command });
    }
    const trimmed = cleaned.trim();
    if (!trimmed) return null;
    return trimmed.length > 72 ? `${trimmed.slice(0, 69)}...` : trimmed;
}

function lspExtensions(
    config: LspConfig,
    lspEnabled: boolean,
    nextDocumentVersion: () => number,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    tooltipParent?: HTMLElement | null,
    completionSources: readonly CompletionSource[] = [],
    hoverSource?: LspHoverSource,
    onDiagnosticSummary?: (summary: LspDiagnosticSummary) => void,
): Extension[] {
    return [
        diagnosticSummaryExtension(onDiagnosticSummary),
        tooltips({
            parent: tooltipParent ?? undefined,
            position: "fixed",
            tooltipSpace: lspTooltipSpace,
        }),
        autocompletion({
            activateOnTyping: true,
            activateOnTypingDelay: 80,
            closeOnBlur: true,
            defaultKeymap: false,
            icons: true,
            interactionDelay: 0,
            optionClass: () => "cm-lsp-completion-option",
            tooltipClass: () => "cm-lsp-completion-tooltip",
            override: [
                ...completionSources,
                (context) => config.languageId === "json" ? jsonLocalCompletionResult(context) : null,
                (context) => config.languageId === "mermaid" ? mermaidLocalCompletionResult(context) : null,
                (context) => config.languageId === "sql" ? sqlLocalCompletionResult(context) : null,
                async (context) => requestLspCompletions(
                    config,
                    context,
                    lspEnabled,
                    nextDocumentVersion,
                    waitForDocumentOpen,
                ),
            ],
        }),
        Prec.highest(keymap.of(lspFeatureKeymap(
            config,
            nextDocumentVersion,
            waitForDocumentOpen,
        ))),
        ...(config.languageId === "json"
            ? [linter((view) => jsonSyntaxDiagnostics(view.state.doc.toString()), { delay: 300 })]
            : []),
        lspHoverExtension(config, waitForDocumentOpen, nextDocumentVersion, hoverSource),
        lspSignatureHelpExtension(config, waitForDocumentOpen, nextDocumentVersion),
        linter(async (view) => {
            if (!config.callSidecar) return [];
            try {
                const result = await callLsp(config, "lsp/diagnostics", {
                    serverId: config.serverId,
                    uri: config.uri,
                });
                const diagnostics = (result as { diagnostics?: LspDiagnostic[] })?.diagnostics;
                return diagnostics?.map((d) => toCodeMirrorDiagnostic(view.state.doc, d, {
                    quickFix: config.callSidecar
                        ? (actionView, from, to) => {
                            void showLspCodeActionsForRange(
                                config,
                                actionView,
                                waitForDocumentOpen,
                                nextDocumentVersion,
                                from,
                                to,
                                [d],
                            );
                        }
                        : undefined,
                })) ?? [];
            } catch {
                return [];
            }
        }, { delay: 700 }),
    ];
}

function lspFeatureKeymap(
    config: LspConfig,
    nextDocumentVersion: () => number,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
) {
    return [
        {
            key: "F8",
            run: (view: EditorView) => nextDiagnostic(view),
        },
        {
            key: "Shift-F8",
            run: (view: EditorView) => previousDiagnostic(view),
        },
        {
            key: "Mod-.",
            run: (view: EditorView) => {
                if (!config.callSidecar) return false;
                void showLspCodeActionsAtCursor(
                    config,
                    view,
                    waitForDocumentOpen,
                    nextDocumentVersion,
                );
                return true;
            },
        },
        {
            key: "Mod-Shift-Space",
            run: (view: EditorView) => {
                if (!config.callSidecar) return false;
                void showLspSignatureHelp(
                    config,
                    view,
                    waitForDocumentOpen,
                    nextDocumentVersion,
                    view.state.selection.main.head,
                );
                return true;
            },
        },
        {
            key: "Alt-Shift-f",
            run: (view: EditorView) => {
                if (!config.callSidecar) return false;
                void formatLspDocument(config, view, waitForDocumentOpen, nextDocumentVersion);
                return true;
            },
        },
        {
            key: "F2",
            run: (view: EditorView) => {
                if (!config.callSidecar) return false;
                void renameLspSymbol(config, view, waitForDocumentOpen, nextDocumentVersion);
                return true;
            },
        },
    ];
}

export function tooltipViewportSpace(
    clientWidth: number,
    clientHeight: number,
    margin = 8,
): { top: number; left: number; bottom: number; right: number } {
    return {
        top: margin,
        left: margin,
        bottom: Math.max(margin, clientHeight - margin),
        right: Math.max(margin, clientWidth - margin),
    };
}

function lspTooltipSpace(view: EditorView) {
    const docElement = view.dom.ownerDocument.documentElement;
    return tooltipViewportSpace(docElement.clientWidth, docElement.clientHeight);
}

function diagnosticSummaryExtension(
    onDiagnosticSummary?: (summary: LspDiagnosticSummary) => void,
): Extension {
    if (!onDiagnosticSummary) return [];
    return ViewPlugin.fromClass(class {
        private summary: LspDiagnosticSummary | null = null;

        constructor(private readonly view: EditorView) {
            this.updateSummary();
        }

        update() {
            this.updateSummary();
        }

        private updateSummary() {
            const next = lspDiagnosticSummaryForState(this.view.state);
            if (this.summary && sameDiagnosticSummary(this.summary, next)) return;
            this.summary = next;
            onDiagnosticSummary(next);
        }
    });
}

function lspDiagnosticSummaryForState(state: EditorState): LspDiagnosticSummary {
    const diagnostics: Diagnostic[] = [];
    forEachDiagnostic(state, (diagnostic) => diagnostics.push(diagnostic));
    return lspDiagnosticSummaryFromDiagnostics(diagnostics);
}

function sameDiagnosticSummary(left: LspDiagnosticSummary, right: LspDiagnosticSummary): boolean {
    return (
        left.errors === right.errors &&
        left.warnings === right.warnings &&
        left.infos === right.infos &&
        left.total === right.total
    );
}

async function openDocument(
    config: LspConfig,
    text: string,
    version: number,
    isCancelled?: () => boolean,
) {
    markLspDocumentOpen(config);
    await ensureServer(config);
    if (isCancelled?.()) return;
    await callLsp(config, "lsp/document.open", {
        serverId: config.serverId,
        uri: config.uri,
        languageId: config.languageId,
        text,
        version,
    });
}

function lspDocumentKey(config: Pick<LspConfig, "serverId" | "uri">): string {
    return `${config.serverId}\n${config.uri}`;
}

function markLspDocumentOpen(config: Pick<LspConfig, "serverId" | "uri">): number {
    const key = lspDocumentKey(config);
    const nextGeneration = (lspDocumentGenerations.get(key) ?? 0) + 1;
    lspDocumentGenerations.set(key, nextGeneration);
    const timer = lspDocumentCloseTimers.get(key);
    if (timer) {
        clearTimeout(timer);
        lspDocumentCloseTimers.delete(key);
    }
    return nextGeneration;
}

function scheduleLspDocumentClose(config: LspConfig) {
    const key = lspDocumentKey(config);
    const scheduledGeneration = lspDocumentGenerations.get(key) ?? 0;
    const existingTimer = lspDocumentCloseTimers.get(key);
    if (existingTimer) clearTimeout(existingTimer);
    const timer = setTimeout(() => {
        lspDocumentCloseTimers.delete(key);
        const currentGeneration = lspDocumentGenerations.get(key) ?? 0;
        if (!shouldCloseScheduledLspDocument(currentGeneration, scheduledGeneration)) return;
        void callLsp(config, "lsp/document.close", {
            serverId: config.serverId,
            uri: config.uri,
        });
    }, 750);
    lspDocumentCloseTimers.set(key, timer);
}

export function shouldCloseScheduledLspDocument(
    currentGeneration: number,
    scheduledGeneration: number,
): boolean {
    return currentGeneration === scheduledGeneration;
}

export function shouldAwaitPendingLspOpen(
    pendingKey: string | null | undefined,
    configKey: string,
): boolean {
    return pendingKey === configKey;
}

async function ensureServer(config: LspConfig) {
    await callLsp(config, "lsp/server.ensure", {
        serverId: config.serverId,
        command: config.command,
        args: config.args,
        rootUri: config.rootUri,
        workingDirectory: config.workingDirectory,
        configFile: config.configFile,
        initializationOptions: config.initializationOptions,
        settings: config.settings,
    });
}

async function callLsp(config: LspConfig, method: string, params: Record<string, unknown>) {
    if (!config.callSidecar) throw new Error("LSP sidecar is unavailable");
    const result = await config.callSidecar("lsp", method, params);
    if (!result.success) throw new Error(result.error ?? `LSP call failed: ${method}`);
    return result.result;
}

function lspHoverExtension(
    config: LspConfig,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
    hoverSource?: LspHoverSource,
    hoverTime = 300,
): Extension {
    return [
        lspHoverTooltipField,
        ViewPlugin.fromClass(class {
            private activeRange: { from: number; to: number } | null = null;
            private closeTimer: ReturnType<typeof setTimeout> | null = null;
            private destroyed = false;
            private hoverTimer: ReturnType<typeof setTimeout> | null = null;
            private lastMouse: { x: number; y: number } | null = null;
            private requestId = 0;
            private tooltipDom: HTMLElement | null = null;

            constructor(private readonly view: EditorView) {
                view.dom.addEventListener("mousemove", this.handleEditorMouseMove);
                view.dom.addEventListener("mouseleave", this.handleEditorMouseLeave);
                view.dom.ownerDocument.addEventListener("mousemove", this.handleDocumentMouseMove, true);
                view.dom.ownerDocument.addEventListener("scroll", this.handleDocumentScroll, true);
            }

            update(update: ViewUpdate) {
                if (update.docChanged) this.clearHoverState();
            }

            destroy() {
                this.destroyed = true;
                this.clearHoverTimer();
                this.clearCloseTimer();
                this.detachTooltipListeners();
                this.view.dom.removeEventListener("mousemove", this.handleEditorMouseMove);
                this.view.dom.removeEventListener("mouseleave", this.handleEditorMouseLeave);
                this.view.dom.ownerDocument.removeEventListener("mousemove", this.handleDocumentMouseMove, true);
                this.view.dom.ownerDocument.removeEventListener("scroll", this.handleDocumentScroll, true);
            }

            private readonly handleEditorMouseMove = (event: MouseEvent) => {
                if (!shouldRequestHover(config.callSidecar, hoverSource)) return;
                this.lastMouse = { x: event.clientX, y: event.clientY };
                if (this.isPointOverActiveRange(event.clientX, event.clientY)) {
                    this.clearCloseTimer();
                    return;
                }

                const pos = pointerDocumentPos(this.view, event.clientX, event.clientY);
                if (pos === null) {
                    this.scheduleClose();
                    return;
                }

                const line = this.view.state.doc.lineAt(pos);
                const range = hoverTokenRange(line.text, line.from, pos);
                if (range.from === range.to) {
                    this.scheduleClose();
                    return;
                }

                if (this.activeRange && sameHoverRange(this.activeRange, range)) {
                    this.clearCloseTimer();
                    return;
                }

                this.scheduleOpen(pos, range);
            };

            private readonly handleEditorMouseLeave = (event: MouseEvent) => {
                const nextTarget = eventTargetNode(event.relatedTarget);
                if (nextTarget && this.tooltipDom?.contains(nextTarget)) {
                    this.clearCloseTimer();
                    return;
                }
                this.scheduleClose();
            };

            private readonly handleDocumentMouseMove = (event: MouseEvent) => {
                if (!this.activeRange && !this.hoverTimer) return;
                const path = event.composedPath();
                if (
                    path.includes(this.view.dom) ||
                    (this.tooltipDom && path.includes(this.tooltipDom))
                ) {
                    this.clearCloseTimer();
                    return;
                }
                if (this.isPointOverActiveRange(event.clientX, event.clientY)) {
                    this.clearCloseTimer();
                    return;
                }
                this.scheduleClose();
            };

            private readonly handleDocumentScroll = (event: Event) => {
                const path = event.composedPath();
                if (this.tooltipDom && path.includes(this.tooltipDom)) return;
                this.closeHover();
            };

            private readonly handleTooltipMouseEnter = () => {
                this.clearCloseTimer();
            };

            private readonly handleTooltipMouseLeave = (event: MouseEvent) => {
                const nextTarget = eventTargetNode(event.relatedTarget);
                if (nextTarget && this.view.dom.contains(nextTarget)) {
                    this.clearCloseTimer();
                    return;
                }
                this.scheduleClose();
            };

            private scheduleOpen(pos: number, range: { from: number; to: number }) {
                this.clearCloseTimer();
                this.clearHoverTimer();
                const requestId = ++this.requestId;
                this.hoverTimer = setTimeout(() => {
                    this.hoverTimer = null;
                    void this.openHover(requestId, pos, range);
                }, hoverTime);
            }

            private async openHover(
                requestId: number,
                pos: number,
                range: { from: number; to: number },
            ) {
                try {
                    const contents = await loadLspHoverContents(
                        config,
                        this.view,
                        waitForDocumentOpen,
                        nextDocumentVersion,
                        pos,
                        range,
                        hoverSource,
                    );
                    if (
                        this.destroyed ||
                        requestId !== this.requestId ||
                        !contents ||
                        !this.isPointerStillOnHoverTarget(range)
                    ) {
                        return;
                    }
                    this.activeRange = range;
                    this.view.dispatch({
                        effects: setLspHoverTooltip.of(createLspHoverTooltip(
                            contents,
                            range,
                            shouldShowHoverAbove(this.view, pos),
                            (dom) => this.setTooltipDom(dom),
                        )),
                    });
                } catch {
                    if (requestId === this.requestId) this.closeHover();
                }
            }

            private scheduleClose(delay = 100) {
                this.clearHoverTimer();
                if (this.closeTimer) return;
                this.closeTimer = setTimeout(() => {
                    this.closeTimer = null;
                    if (!this.lastMouse || !this.isPointerStillOnHoverTarget(this.activeRange)) {
                        this.closeHover();
                    }
                }, delay);
            }

            private closeHover() {
                this.clearHoverState();
                if (!this.destroyed) {
                    this.view.dispatch({ effects: setLspHoverTooltip.of(null) });
                }
            }

            private clearHoverState() {
                this.clearHoverTimer();
                this.clearCloseTimer();
                this.requestId += 1;
                this.activeRange = null;
                this.detachTooltipListeners();
            }

            private clearHoverTimer() {
                if (this.hoverTimer) {
                    clearTimeout(this.hoverTimer);
                    this.hoverTimer = null;
                }
            }

            private clearCloseTimer() {
                if (this.closeTimer) {
                    clearTimeout(this.closeTimer);
                    this.closeTimer = null;
                }
            }

            private isPointOverActiveRange(x: number, y: number): boolean {
                return this.activeRange ? isPointOverRange(this.view, this.activeRange, x, y) : false;
            }

            private isPointerStillOnHoverTarget(range: { from: number; to: number } | null): boolean {
                if (!this.lastMouse) return false;
                if (this.tooltipDom && isPointInElement(this.tooltipDom, this.lastMouse.x, this.lastMouse.y)) {
                    return true;
                }
                return range ? isPointOverRange(this.view, range, this.lastMouse.x, this.lastMouse.y) : false;
            }

            private setTooltipDom(dom: HTMLElement | null) {
                this.detachTooltipListeners();
                this.tooltipDom = dom;
                if (!dom) return;
                dom.addEventListener("mouseenter", this.handleTooltipMouseEnter);
                dom.addEventListener("mouseleave", this.handleTooltipMouseLeave);
            }

            private detachTooltipListeners() {
                if (!this.tooltipDom) return;
                this.tooltipDom.removeEventListener("mouseenter", this.handleTooltipMouseEnter);
                this.tooltipDom.removeEventListener("mouseleave", this.handleTooltipMouseLeave);
                this.tooltipDom = null;
            }
        }),
    ];
}

function lspSignatureHelpExtension(
    config: LspConfig,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
): Extension {
    return [
        lspSignatureTooltipField,
        ViewPlugin.fromClass(class {
            private requestId = 0;
            private timer: ReturnType<typeof setTimeout> | null = null;

            constructor(private readonly view: EditorView) {}

            update(update: ViewUpdate) {
                if (!config.callSidecar) return;
                if (update.docChanged) {
                    const triggerCharacter = lspSignatureTriggerCharacter(update);
                    if (triggerCharacter === ")") {
                        this.close();
                        return;
                    }
                    if (triggerCharacter) {
                        this.schedule(update.state.selection.main.head, triggerCharacter);
                        return;
                    }
                }
                if (update.selectionSet) this.close();
            }

            destroy() {
                this.clearTimer();
            }

            private schedule(pos: number, triggerCharacter?: string) {
                this.clearTimer();
                const requestId = ++this.requestId;
                this.timer = setTimeout(() => {
                    this.timer = null;
                    if (requestId !== this.requestId) return;
                    void showLspSignatureHelp(
                        config,
                        this.view,
                        waitForDocumentOpen,
                        nextDocumentVersion,
                        pos,
                        triggerCharacter,
                    );
                }, 80);
            }

            private close() {
                this.clearTimer();
                this.requestId += 1;
                this.view.dispatch({ effects: setLspSignatureTooltip.of(null) });
            }

            private clearTimer() {
                if (!this.timer) return;
                clearTimeout(this.timer);
                this.timer = null;
            }
        }),
    ];
}

function lspSignatureTriggerCharacter(update: ViewUpdate): string | null {
    let trigger: string | null = null;
    update.changes.iterChanges((_fromA, _toA, _fromB, _toB, inserted) => {
        const text = inserted.toString();
        if (text === "(" || text === "," || text === ")") {
            trigger = text;
        }
    });
    return trigger;
}

function createLspHoverTooltip(
    contents: string,
    range: { from: number; to: number },
    above: boolean,
    onDomChange: (dom: HTMLElement | null) => void,
): Tooltip {
    return {
        pos: range.from,
        end: range.to,
        above,
        clip: false,
        create(view) {
            const dom = view.dom.ownerDocument.createElement("div");
            dom.className = "cm-tooltip-hover cm-lsp-hover";
            dom.innerHTML = markdownToHtml(contents);
            onDomChange(dom);
            return {
                dom,
                offset: { x: 0, y: -2 },
                destroy() {
                    onDomChange(null);
                },
            };
        },
    };
}

function pointerDocumentPos(view: EditorView, x: number, y: number): number | null {
    const pos = view.posAtCoords({ x, y });
    if (pos === null) return null;
    const coords = view.coordsAtPos(pos);
    if (!coords) return null;
    return y >= coords.top - 2 && y <= coords.bottom + 2 ? pos : null;
}

function isPointOverRange(
    view: EditorView,
    range: { from: number; to: number },
    x: number,
    y: number,
): boolean {
    const pos = pointerDocumentPos(view, x, y);
    return pos !== null && pos >= range.from && pos <= range.to;
}

function sameHoverRange(
    left: { from: number; to: number },
    right: { from: number; to: number },
): boolean {
    return left.from === right.from && left.to === right.to;
}

function eventTargetNode(target: EventTarget | null): Node | null {
    return target instanceof Node ? target : null;
}

function isPointInElement(element: HTMLElement, x: number, y: number): boolean {
    const rect = element.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

async function loadLspHoverContents(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
    pos: number,
    range: { from: number; to: number },
    hoverSource?: LspHoverSource,
): Promise<string | null> {
    const line = view.state.doc.lineAt(pos);
    if (config.callSidecar) {
        try {
            await waitForDocumentOpen(config);
            await ensureServer(config);
            await syncLspDocument(config, view, nextDocumentVersion);
            const result = await callLsp(config, "lsp/hover", {
                serverId: config.serverId,
                uri: config.uri,
                line: line.number - 1,
                character: pos - line.from,
            });
            const contents = hoverContentsToString((result as { contents?: unknown })?.contents);
            if (contents) return contents;
        } catch {
            // Keep hover useful when the language server is still starting or unavailable.
        }
    }
    if (!hoverSource) return null;
    return hoverSource({
        token: view.state.sliceDoc(range.from, range.to),
        range,
        lineText: line.text,
        pos,
        languageId: config.languageId,
    });
}

async function showLspSignatureHelp(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
    pos: number,
    triggerCharacter?: string,
) {
    if (!config.callSidecar) return;
    try {
        const help = await loadLspSignatureHelp(
            config,
            view,
            waitForDocumentOpen,
            nextDocumentVersion,
            pos,
            triggerCharacter,
        );
        const model = lspSignatureHelpToViewModel(help);
        view.dispatch({
            effects: setLspSignatureTooltip.of(model
                ? createLspSignatureTooltip(model, pos)
                : null),
        });
    } catch {
        view.dispatch({ effects: setLspSignatureTooltip.of(null) });
    }
}

async function loadLspSignatureHelp(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
    pos: number,
    triggerCharacter?: string,
): Promise<LspSignatureHelp | null> {
    const line = view.state.doc.lineAt(pos);
    await waitForDocumentOpen(config);
    await ensureServer(config);
    await syncLspDocument(config, view, nextDocumentVersion);
    const result = await callLsp(config, "lsp/signatureHelp", {
        serverId: config.serverId,
        uri: config.uri,
        line: line.number - 1,
        character: pos - line.from,
        context: {
            triggerKind: triggerCharacter ? 2 : 1,
            isRetrigger: false,
            ...(triggerCharacter ? { triggerCharacter } : {}),
        },
    });
    return result && typeof result === "object" ? result as LspSignatureHelp : null;
}

function createLspSignatureTooltip(
    model: LspSignatureViewModel,
    pos: number,
): Tooltip {
    return {
        pos,
        above: false,
        clip: false,
        create(view) {
            const dom = view.dom.ownerDocument.createElement("div");
            dom.className = "cm-tooltip-hover cm-lsp-signature";
            dom.innerHTML = signatureViewModelToHtml(model);
            return { dom, offset: { x: 0, y: 4 } };
        },
    };
}

export function lspSignatureHelpToViewModel(
    help: LspSignatureHelp | null | undefined,
): LspSignatureViewModel | null {
    const signatures = help?.signatures;
    if (!signatures?.length) return null;
    const activeSignature = clampIndex(help?.activeSignature, signatures.length);
    const signature = signatures[activeSignature];
    const label = signature?.label;
    if (!label) return null;
    const parameters = signature.parameters ?? [];
    const activeParameter = clampIndex(
        signature.activeParameter ?? help?.activeParameter,
        parameters.length,
    );
    const parameter = parameters[activeParameter];
    const parameterSpan = signatureParameterSpan(label, parameter?.label);
    return {
        label,
        documentation: documentationToString(signature.documentation),
        parameterLabel: signatureParameterLabel(label, parameter?.label),
        parameterDocumentation: documentationToString(parameter?.documentation),
        parameterSpan,
    };
}

function signatureViewModelToHtml(model: LspSignatureViewModel): string {
    const label = model.parameterSpan
        ? escapeHtml(model.label.slice(0, model.parameterSpan[0]))
            + `<span class="mh-active-param">${escapeHtml(model.label.slice(model.parameterSpan[0], model.parameterSpan[1]))}</span>`
            + escapeHtml(model.label.slice(model.parameterSpan[1]))
        : escapeHtml(model.label);
    const lines = [
        model.parameterDocumentation,
        model.documentation,
    ].filter((line): line is string => Boolean(line?.trim()));

    return [
        `<div class="mh-title">${label}</div>`,
        lines.length
            ? `<div class="mh-body">${lines.map((line) => `<div class="mh-line">${inlineMarkdownToHtml(line)}</div>`).join("")}</div>`
            : "",
    ].join("");
}

function signatureParameterSpan(
    signatureLabel: string,
    parameterLabel: string | [number, number] | undefined,
): [number, number] | undefined {
    if (Array.isArray(parameterLabel)) {
        const [from, to] = parameterLabel;
        if (Number.isFinite(from) && Number.isFinite(to) && from >= 0 && to > from && to <= signatureLabel.length) {
            return [from, to];
        }
        return undefined;
    }
    if (!parameterLabel) return undefined;
    const index = signatureLabel.indexOf(parameterLabel);
    return index >= 0 ? [index, index + parameterLabel.length] : undefined;
}

function signatureParameterLabel(
    signatureLabel: string,
    parameterLabel: string | [number, number] | undefined,
): string | undefined {
    if (Array.isArray(parameterLabel)) {
        const span = signatureParameterSpan(signatureLabel, parameterLabel);
        return span ? signatureLabel.slice(span[0], span[1]) : undefined;
    }
    return parameterLabel;
}

function clampIndex(index: number | undefined, length: number): number {
    if (!length) return 0;
    if (index === undefined || !Number.isFinite(index)) return 0;
    return Math.min(Math.max(index, 0), length - 1);
}

async function requestLspCompletions(
    config: LspConfig,
    context: CompletionContext,
    lspEnabled: boolean,
    nextDocumentVersion: () => number,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
): Promise<CompletionResult | null> {
    if (!shouldRequestCompletions(config, context)) return null;
    if (!lspEnabled || !config.callSidecar) return null;
    const line = context.state.doc.lineAt(context.pos);
    const from = lspCompletionFrom(line.text, line.from, context.pos);
    try {
        await waitForDocumentOpen(config);
        if (context.aborted) return null;
        await ensureServer(config);
        if (context.aborted) return null;
        await callLsp(config, "lsp/document.change", {
            serverId: config.serverId,
            uri: config.uri,
            text: context.state.doc.toString(),
            version: nextDocumentVersion(),
        });
        if (context.aborted) return null;
        const result = await callLsp(config, "lsp/completion", {
            serverId: config.serverId,
            uri: config.uri,
            line: line.number - 1,
            character: context.pos - line.from,
        });
        if (context.aborted) return null;
        const payload = result as { items?: LspCompletionItem[] } | LspCompletionItem[] | undefined;
        const items = Array.isArray(payload) ? payload : payload?.items;
        if (!items?.length) return null;
        const lspOptions = items.map(toCodeMirrorCompletion).filter(Boolean) as Completion[];
        if (!lspOptions.length) return null;
        return {
            from,
            options: lspOptions,
            filter: true,
            validFor: lspCompletionValidFor,
        };
    } catch {
        return null;
    }
}

async function syncLspDocument(
    config: LspConfig,
    view: EditorView,
    nextDocumentVersion: () => number,
) {
    if (!config.callSidecar) return;
    await callLsp(config, "lsp/document.change", {
        serverId: config.serverId,
        uri: config.uri,
        text: view.state.doc.toString(),
        version: nextDocumentVersion(),
    });
}

async function showLspCodeActionsAtCursor(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
) {
    const diagnostic = diagnosticAtSelection(view.state);
    const selection = view.state.selection.main;
    const from = diagnostic?.from ?? Math.min(selection.from, selection.to);
    const to = diagnostic?.to ?? Math.max(selection.from, selection.to);
    await showLspCodeActionsForRange(
        config,
        view,
        waitForDocumentOpen,
        nextDocumentVersion,
        from,
        to,
        [],
    );
}

async function showLspCodeActionsForRange(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
    from: number,
    to: number,
    diagnostics: readonly LspDiagnostic[] = [],
) {
    if (!config.callSidecar) return;
    const actions = await requestLspCodeActions(
        config,
        view,
        waitForDocumentOpen,
        nextDocumentVersion,
        from,
        to,
        diagnostics,
    );
    const action = selectLspCodeAction(view, actions);
    if (!action) return;
    await applyLspCodeAction(config, view, action);
}

async function requestLspCodeActions(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
    from: number,
    to: number,
    diagnostics: readonly LspDiagnostic[] = [],
): Promise<LspCodeAction[]> {
    await waitForDocumentOpen(config);
    await ensureServer(config);
    await syncLspDocument(config, view, nextDocumentVersion);
    const result = await callLsp(config, "lsp/codeAction", {
        serverId: config.serverId,
        uri: config.uri,
        range: lspRangeForOffsets(view.state.doc, from, to),
        context: { diagnostics },
    });
    return Array.isArray(result) ? result.filter(isLspCodeAction) : [];
}

function selectLspCodeAction(
    view: EditorView,
    actions: readonly LspCodeAction[],
): LspCodeAction | null {
    if (!actions.length) return null;
    const preferred = actions.find((action) => action.isPreferred);
    if (preferred) return preferred;
    if (actions.length === 1) return actions[0] ?? null;
    const ownerWindow = view.dom.ownerDocument.defaultView;
    if (!ownerWindow?.prompt) return actions[0] ?? null;
    const menu = actions
        .map((action, index) => `${index + 1}. ${action.title ?? "Code action"}`)
        .join("\n");
    const selected = ownerWindow.prompt(`Quick Fix\n${menu}`, "1");
    const index = selected ? Number.parseInt(selected, 10) - 1 : 0;
    return actions[index] ?? actions[0] ?? null;
}

async function applyLspCodeAction(
    config: LspConfig,
    view: EditorView,
    action: LspCodeAction,
) {
    let resolved = action;
    if (!resolved.edit && resolved.data !== undefined) {
        const result = await callLsp(config, "lsp/codeAction.resolve", {
            serverId: config.serverId,
            codeAction: action,
        });
        if (isLspCodeAction(result)) resolved = result;
    }
    if (!resolved.edit) return;
    applyLspTextEditsToView(view, lspWorkspaceEditTextEditsForUri(resolved.edit, config.uri));
}

function diagnosticAtSelection(state: EditorState): { from: number; to: number } | null {
    const pos = state.selection.main.head;
    let best: { from: number; to: number } | null = null;
    forEachDiagnostic(state, (_diagnostic, from, to) => {
        if (best) return;
        if (pos >= from && pos <= Math.max(from, to)) {
            best = { from, to };
        }
    });
    return best;
}

function isLspCodeAction(value: unknown): value is LspCodeAction {
    return !!value && typeof value === "object" && typeof (value as LspCodeAction).title === "string";
}

function lspRangeForOffsets(doc: Text, from: number, to: number): LspRange {
    return {
        start: offsetToPosition(doc, from),
        end: offsetToPosition(doc, to),
    };
}

async function formatLspDocument(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
) {
    if (!config.callSidecar) return;
    try {
        await waitForDocumentOpen(config);
        await ensureServer(config);
        await syncLspDocument(config, view, nextDocumentVersion);
        const result = await callLsp(config, "lsp/format", {
            serverId: config.serverId,
            uri: config.uri,
            tabSize: 4,
            insertSpaces: true,
        });
        applyLspTextEditsToView(view, Array.isArray(result) ? result as LspTextEdit[] : []);
    } catch {
        // Formatting is an optional editor command. Existing diagnostics/status handle server failures.
    }
}

async function renameLspSymbol(
    config: LspConfig,
    view: EditorView,
    waitForDocumentOpen: (config: LspConfig) => Promise<void>,
    nextDocumentVersion: () => number,
) {
    if (!config.callSidecar) return;
    const pos = view.state.selection.main.head;
    const line = view.state.doc.lineAt(pos);
    const range = hoverTokenRange(line.text, line.from, pos);
    const currentName = range.from < range.to ? view.state.sliceDoc(range.from, range.to) : "";
    const ownerWindow = view.dom.ownerDocument.defaultView;
    if (!ownerWindow?.prompt) return;
    const nextName = ownerWindow.prompt("Rename symbol", currentName);
    if (!nextName || nextName === currentName) return;

    try {
        await waitForDocumentOpen(config);
        await ensureServer(config);
        await syncLspDocument(config, view, nextDocumentVersion);
        const result = await callLsp(config, "lsp/rename", {
            serverId: config.serverId,
            uri: config.uri,
            line: line.number - 1,
            character: pos - line.from,
            newName: nextName,
        });
        applyLspTextEditsToView(view, lspWorkspaceEditTextEditsForUri(result, config.uri));
    } catch {
        // Rename support is server-dependent. Ignore unsupported responses inside the editor.
    }
}

function applyLspTextEditsToView(view: EditorView, edits: readonly LspTextEdit[]) {
    const changes = lspTextEditsToCodeMirrorChanges(view.state.doc, edits);
    if (!changes.length) return;
    view.dispatch({ changes });
}

export function lspWorkspaceEditTextEditsForUri(edit: unknown, uri: string): LspTextEdit[] {
    if (!edit || typeof edit !== "object") return [];
    const workspaceEdit = edit as LspWorkspaceEdit;
    const fromChanges = workspaceEdit.changes?.[uri] ?? [];
    const fromDocumentChanges = (workspaceEdit.documentChanges ?? []).flatMap((change) => {
        if (!change || typeof change !== "object") return [];
        const candidate = change as LspWorkspaceTextDocumentEdit;
        return candidate.textDocument?.uri === uri ? candidate.edits ?? [] : [];
    });
    return [...fromChanges, ...fromDocumentChanges];
}

export function lspTextEditsToCodeMirrorChanges(
    doc: Text,
    edits: readonly LspTextEdit[] | null | undefined,
): Array<{ from: number; to: number; insert: string }> {
    return (edits ?? [])
        .map((edit) => {
            const start = edit.range?.start;
            const end = edit.range?.end;
            const from = positionToOffset(doc, start?.line ?? 0, start?.character ?? 0);
            const to = positionToOffset(
                doc,
                end?.line ?? start?.line ?? 0,
                end?.character ?? start?.character ?? 0,
            );
            return {
                from: Math.min(from, to),
                to: Math.max(from, to),
                insert: edit.newText ?? "",
            };
        })
        .sort((left, right) => left.from - right.from || left.to - right.to);
}

const lspCompletionValidFor = /^[\w$."'-]*$/;

export function isLspCompletionFilterTextValid(text: string): boolean {
    return lspCompletionValidFor.test(text);
}

export function jsonSyntaxDiagnostics(text: string): Diagnostic[] {
    if (!text.trim()) return [];
    try {
        JSON.parse(text);
        return [];
    } catch (err) {
        const message = err instanceof SyntaxError ? err.message : "Invalid JSON";
        const position = jsonSyntaxErrorOffset(message, text.length) ?? firstNonWhitespaceOffset(text);
        return [{
            from: position,
            to: Math.min(text.length, position + 1),
            severity: "error",
            message,
            source: "json",
        }];
    }
}

export function jsonSyntaxErrorOffset(message: string, textLength: number): number | null {
    const match = /\bposition\s+(\d+)\b/i.exec(message);
    if (!match) return null;
    const value = Number.parseInt(match[1], 10);
    return Number.isFinite(value) ? Math.min(Math.max(0, value), textLength) : null;
}

function firstNonWhitespaceOffset(text: string): number {
    const match = /\S/.exec(text);
    return match?.index ?? 0;
}

function shouldRequestCompletions(config: LspConfig, context: CompletionContext): boolean {
    if (context.explicit) return true;
    const line = context.state.doc.lineAt(context.pos);
    const before = line.text.slice(0, context.pos - line.from);
    if (config.languageId === "sql" && sqlPhraseCompletionFrom(before) !== null) return true;
    if (config.languageId === "mermaid" && mermaidPhraseCompletionFrom(before) !== null) return true;
    return /[.\w"'`:-]$/.test(before);
}

export function jsonLocalCompletionResult(context: CompletionContext): CompletionResult | null {
    if (!context.explicit && !shouldOfferJsonLocalCompletion(context)) return null;
    return buildJsonLocalCompletionResult(context.state.doc.toString(), context.pos);
}

function shouldOfferJsonLocalCompletion(context: CompletionContext): boolean {
    const line = context.state.doc.lineAt(context.pos);
    const before = line.text.slice(0, context.pos - line.from);
    return /[\w"$]$/.test(before) || /[{[,]\s*$/.test(before) || /:\s*$/.test(before);
}

export function buildJsonLocalCompletionResult(text: string, pos: number): CompletionResult | null {
    const before = text.slice(0, pos);
    const quotedKey = before.match(/"([^"\\]*)$/);
    const word = before.match(/[A-Za-z_$][\w$-]*$/);
    const from = quotedKey ? pos - quotedKey[1].length : word ? pos - word[0].length : pos;
    const options = isLikelyJsonKeyContext(before)
        ? jsonKeyCompletions
        : jsonValueCompletions;
    return { from, options, filter: true, validFor: /^[\w$-]*$/ };
}

function isLikelyJsonKeyContext(before: string): boolean {
    const trimmed = before.trimEnd();
    if (!trimmed) return true;
    if (/:$/.test(trimmed)) return false;
    const lastStructural = Math.max(
        trimmed.lastIndexOf("{"),
        trimmed.lastIndexOf(","),
        trimmed.lastIndexOf("["),
    );
    if (lastStructural < 0) return false;
    const segment = trimmed.slice(lastStructural);
    return segment.startsWith("{") || (segment.startsWith(",") && !segment.includes(":"));
}

const jsonKeyCompletions: Completion[] = [
    snippetCompletion('"${1:key}": ${2:null}', {
        label: '"key": value',
        type: "property",
        detail: "JSON property",
        boost: 6,
    }),
    snippetCompletion('"${1:key}": {\n  ${2}\n}', {
        label: '"key": {}',
        type: "property",
        detail: "object property",
        boost: 5,
    }),
    snippetCompletion('"${1:key}": [\n  ${2}\n]', {
        label: '"key": []',
        type: "property",
        detail: "array property",
        boost: 5,
    }),
];

const jsonValueCompletions: Completion[] = [
    { label: "true", type: "constant", detail: "boolean", boost: 6 },
    { label: "false", type: "constant", detail: "boolean", boost: 6 },
    { label: "null", type: "constant", detail: "null", boost: 6 },
    snippetCompletion('"${1:value}"', {
        label: "string",
        type: "snippet",
        detail: "JSON string",
        boost: 4,
    }),
    snippetCompletion("{\n  ${1}\n}", {
        label: "object",
        type: "snippet",
        detail: "JSON object",
        boost: 4,
    }),
    snippetCompletion("[\n  ${1}\n]", {
        label: "array",
        type: "snippet",
        detail: "JSON array",
        boost: 4,
    }),
];

export function mermaidLocalCompletionResult(context: CompletionContext): CompletionResult | null {
    if (!context.explicit && !shouldOfferMermaidLocalCompletion(context)) return null;
    return buildMermaidLocalCompletionResult(context.state.doc.toString(), context.pos);
}

function shouldOfferMermaidLocalCompletion(context: CompletionContext): boolean {
    const line = context.state.doc.lineAt(context.pos);
    const before = line.text.slice(0, context.pos - line.from);
    return /[A-Za-z_-]$/.test(before) || mermaidPhraseCompletionFrom(before) !== null;
}

export function buildMermaidLocalCompletionResult(text: string, pos: number): CompletionResult | null {
    const before = text.slice(0, pos);
    const phraseFrom = mermaidPhraseCompletionFrom(before);
    const word = before.match(/[A-Za-z][\w-]*$/);
    const from = phraseFrom ?? (word ? pos - word[0].length : pos);
    return {
        from,
        options: mermaidCompletionOptions,
        filter: true,
        validFor: /^[\w-]*$/,
    };
}

function mermaidPhraseCompletionFrom(before: string): number | null {
    const lower = before.toLowerCase();
    const phraseMatch = lower.match(/\b(?:note|sequence|state|class|flow|graph|date|axis)\s+\w*$/);
    return phraseMatch ? before.length - phraseMatch[0].length : null;
}

const mermaidCompletionOptions: Completion[] = [
    snippetCompletion("flowchart ${1:TD}\n    ${2:A}[${3:Start}] --> ${4:B}[${5:End}]", {
        label: "flowchart",
        type: "snippet",
        detail: "flowchart diagram",
        boost: 10,
    }),
    snippetCompletion("sequenceDiagram\n    participant ${1:A}\n    participant ${2:B}\n    ${1:A}->>${2:B}: ${3:message}", {
        label: "sequenceDiagram",
        type: "snippet",
        detail: "sequence diagram",
        boost: 10,
    }),
    snippetCompletion("classDiagram\n    class ${1:ClassName} {\n        +${2:method}()\n    }", {
        label: "classDiagram",
        type: "snippet",
        detail: "class diagram",
        boost: 9,
    }),
    snippetCompletion("stateDiagram-v2\n    [*] --> ${1:State}\n    ${1:State} --> [*]", {
        label: "stateDiagram-v2",
        type: "snippet",
        detail: "state diagram",
        boost: 9,
    }),
    snippetCompletion("erDiagram\n    ${1:CUSTOMER} ||--o{ ${2:ORDER} : ${3:places}", {
        label: "erDiagram",
        type: "snippet",
        detail: "ER diagram",
        boost: 9,
    }),
    snippetCompletion("gantt\n    title ${1:Project}\n    dateFormat YYYY-MM-DD\n    section ${2:Phase}\n    ${3:Task} :${4:active}, ${5:2026-01-01}, ${6:7d}", {
        label: "gantt",
        type: "snippet",
        detail: "gantt chart",
        boost: 8,
    }),
    snippetCompletion("pie showData\n    title ${1:Breakdown}\n    \"${2:A}\" : ${3:40}\n    \"${4:B}\" : ${5:60}", {
        label: "pie",
        type: "snippet",
        detail: "pie chart",
        boost: 8,
    }),
    ...[
        "graph",
        "journey",
        "mindmap",
        "timeline",
        "gitGraph",
        "quadrantChart",
        "xychart-beta",
        "participant",
        "actor",
        "activate",
        "deactivate",
        "loop",
        "alt",
        "else",
        "opt",
        "par",
        "and",
        "Note over",
        "section",
        "title",
        "dateFormat",
        "axisFormat",
        "direction",
        "TD",
        "LR",
        "RL",
        "BT",
        "-->",
        "---",
        "-.->",
        "==>",
        "-->|label|",
    ].map((label): Completion => ({
        label,
        type: mermaidKeywords.includes(label as (typeof mermaidKeywords)[number]) ? "keyword" : "text",
        boost: label.includes(">") || label === "TD" || label === "LR" ? 5 : 3,
    })),
];

export function mermaidFoldRange(doc: Text, lineNumber: number): { from: number; to: number } | null {
    const line = doc.line(lineNumber);
    const startText = line.text.trim();
    if (!startText || startText.startsWith("%%") || lineNumber >= doc.lines) return null;
    const blockRange = mermaidBlockFoldRange(doc, lineNumber, startText);
    if (blockRange) return blockRange;
    return indentationFoldRange(doc, lineNumber);
}

function mermaidBlockFoldRange(
    doc: Text,
    lineNumber: number,
    startText: string,
): { from: number; to: number } | null {
    const lower = startText.toLowerCase();
    if (/^(?:loop|alt|opt|par|critical|break|rect|box)\b/.test(lower)) {
        let depth = 0;
        for (let index = lineNumber + 1; index <= doc.lines; index += 1) {
            const candidate = doc.line(index);
            const text = candidate.text.trim().toLowerCase();
            if (/^(?:loop|alt|opt|par|critical|break|rect|box)\b/.test(text)) depth += 1;
            if (text === "end") {
                if (depth === 0) return foldBetweenLines(doc.line(lineNumber), candidate);
                depth -= 1;
            }
        }
    }
    if (/^section\b/.test(lower)) {
        let endLine = doc.line(doc.lines);
        for (let index = lineNumber + 1; index <= doc.lines; index += 1) {
            const candidate = doc.line(index);
            if (index > lineNumber + 1 && /^section\b/i.test(candidate.text.trim())) {
                endLine = doc.line(index - 1);
                break;
            }
        }
        return foldBetweenLines(doc.line(lineNumber), endLine);
    }
    return null;
}

function indentationFoldRange(doc: Text, lineNumber: number): { from: number; to: number } | null {
    const line = doc.line(lineNumber);
    const baseIndent = leadingWhitespaceLength(line.text);
    let sawIndented = false;
    let endLine = line;
    for (let index = lineNumber + 1; index <= doc.lines; index += 1) {
        const candidate = doc.line(index);
        if (!candidate.text.trim()) continue;
        const indent = leadingWhitespaceLength(candidate.text);
        if (indent <= baseIndent) break;
        sawIndented = true;
        endLine = candidate;
    }
    return sawIndented ? foldBetweenLines(line, endLine) : null;
}

function foldBetweenLines(
    startLine: ReturnType<Text["line"]>,
    endLine: ReturnType<Text["line"]>,
): { from: number; to: number } | null {
    if (endLine.number <= startLine.number) return null;
    return { from: startLine.to, to: endLine.to };
}

function leadingWhitespaceLength(text: string): number {
    return text.match(/^\s*/)?.[0].length ?? 0;
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function sqlLocalCompletionResult(context: CompletionContext): CompletionResult | null {
    if (!context.explicit && !shouldOfferSqlLocalCompletion(context)) return null;
    return buildSqlLocalCompletionResult(context.state.doc.toString(), context.pos);
}

function shouldOfferSqlLocalCompletion(context: CompletionContext): boolean {
    const line = context.state.doc.lineAt(context.pos);
    const before = line.text.slice(0, context.pos - line.from);
    return /[A-Za-z_][\w$]*$/.test(before) || sqlPhraseCompletionFrom(before) !== null;
}

export function buildSqlLocalCompletionResult(text: string, pos: number): CompletionResult | null {
    const before = text.slice(0, pos);
    const phraseFrom = sqlPhraseCompletionFrom(before);
    const word = before.match(/[A-Za-z_][\w$]*$/);
    const from = phraseFrom ?? (word ? pos - word[0].length : pos);
    const phraseOptions = phraseFrom === null ? [] : sqlPhraseCompletions(before);
    const options = mergeCompletionOptions(
        phraseOptions,
        [
            ...sqlFunctionCompletions,
            ...sqlKeywordCompletions,
        ],
    );

    return options.length
        ? { from, options, filter: true, validFor: lspCompletionValidFor }
        : null;
}

const sqlKeywordLabels = [
    "select",
    "from",
    "where",
    "join",
    "left join",
    "right join",
    "inner join",
    "full join",
    "cross join",
    "group by",
    "order by",
    "having",
    "limit",
    "offset",
    "with",
    "insert",
    "update",
    "delete",
    "returning",
    "distinct",
    "case",
    "when",
    "then",
    "else",
    "end",
    "and",
    "or",
    "not",
    "is null",
    "is not null",
] as const;

const sqlKeywordCompletions: Completion[] = sqlKeywordLabels.map((label) => ({
    label,
    type: "keyword",
    detail: "SQL keyword",
    apply: label.includes(" ") ? `${label} ` : label,
    boost: label === "group by" || label === "order by" ? 60 : 0,
}));

const sqlFunctionCompletions: Completion[] = [
    snippetCompletion("count(${1:*})", {
        label: "count",
        type: "function",
        detail: "SQL aggregate",
        boost: 70,
    }),
    snippetCompletion("sum(${1:expression})", {
        label: "sum",
        type: "function",
        detail: "SQL aggregate",
    }),
    snippetCompletion("avg(${1:expression})", {
        label: "avg",
        type: "function",
        detail: "SQL aggregate",
    }),
    snippetCompletion("min(${1:expression})", {
        label: "min",
        type: "function",
        detail: "SQL aggregate",
    }),
    snippetCompletion("max(${1:expression})", {
        label: "max",
        type: "function",
        detail: "SQL aggregate",
    }),
    snippetCompletion("coalesce(${1:value}, ${2:fallback})", {
        label: "coalesce",
        type: "function",
        detail: "SQL function",
    }),
    snippetCompletion("date_trunc('${1:day}', ${2:timestamp})", {
        label: "date_trunc",
        type: "function",
        detail: "PostgreSQL function",
    }),
    snippetCompletion("extract(${1:field} from ${2:source})", {
        label: "extract",
        type: "function",
        detail: "SQL function",
    }),
    snippetCompletion("round(${1:value})", {
        label: "round",
        type: "function",
        detail: "SQL function",
    }),
    snippetCompletion("lower(${1:text})", {
        label: "lower",
        type: "function",
        detail: "SQL function",
    }),
    snippetCompletion("upper(${1:text})", {
        label: "upper",
        type: "function",
        detail: "SQL function",
    }),
    snippetCompletion("concat(${1:value})", {
        label: "concat",
        type: "function",
        detail: "SQL function",
    }),
    snippetCompletion("jsonb_build_object(${1:key}, ${2:value})", {
        label: "jsonb_build_object",
        type: "function",
        detail: "PostgreSQL function",
    }),
    snippetCompletion("row_number() over (${1:order by })", {
        label: "row_number",
        type: "function",
        detail: "SQL window function",
    }),
    snippetCompletion("rank() over (${1:order by })", {
        label: "rank",
        type: "function",
        detail: "SQL window function",
    }),
    {
        label: "now",
        type: "function",
        detail: "PostgreSQL function",
        apply: "now()",
    },
];

function sqlPhraseCompletionFrom(textBefore: string): number | null {
    const phrase = textBefore.match(/\b(group|order|left|right|inner|full|cross)\s+$/i);
    return phrase ? textBefore.length - phrase[0].length : null;
}

function sqlPhraseCompletions(textBefore: string): Completion[] {
    const phrase = textBefore.match(/\b(group|order|left|right|inner|full|cross)\s+$/i);
    if (!phrase) return [];
    const keyword = phrase[1].toLowerCase();
    if (keyword === "group" || keyword === "order") {
        return [{
            label: `${keyword} by`,
            type: "keyword",
            detail: "SQL clause",
            apply: `${keyword} by `,
            boost: 100,
        }];
    }
    return [{
        label: `${keyword} join`,
        type: "keyword",
        detail: "SQL join",
        apply: `${keyword} join `,
        boost: 100,
    }];
}

function mergeCompletionOptions(
    primary: readonly Completion[],
    secondary: readonly Completion[],
): Completion[] {
    const seen = new Set<string>();
    const merged: Completion[] = [];
    for (const option of [...primary, ...secondary]) {
        const key = `${option.label}:${option.type ?? ""}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(option);
    }
    return merged;
}

function shouldShowHoverAbove(view: EditorView, pos: number): boolean {
    const coords = view.coordsAtPos(pos);
    if (!coords) return false;
    const docElement = view.dom.ownerDocument.documentElement;
    const spaceAbove = coords.top;
    const spaceBelow = docElement.clientHeight - coords.bottom;
    return spaceAbove > 180 && spaceAbove > spaceBelow;
}

export function hoverTokenRange(
    lineText: string,
    lineFrom: number,
    pos: number,
): { from: number; to: number } {
    const localPos = Math.min(Math.max(pos - lineFrom, 0), lineText.length);
    let start = localPos;
    let end = localPos;
    while (start > 0 && /[\w$."']/.test(lineText[start - 1])) start -= 1;
    while (end < lineText.length && /[\w$."']/.test(lineText[end])) end += 1;
    if (end <= start) return { from: pos, to: pos };
    return { from: lineFrom + start, to: lineFrom + end };
}

function toCodeMirrorCompletion(item: LspCompletionItem): Completion | null {
    const label = item.label;
    if (!label) return null;
    const applyText = item.textEdit?.newText ?? item.insertText ?? label;
    const common = {
        label,
        type: item.kind ? completionKindMap[item.kind] ?? "text" : "text",
        detail: item.detail,
        info: documentationToString(item.documentation),
    };
    return item.insertTextFormat === 2
        ? snippetCompletion(snippetToCodeMirror(applyText), common)
        : { ...common, apply: applyText };
}

function snippetToCodeMirror(value: string): string {
    return value.replace(/\$(\d+)/g, (_m, index: string) => `\${${index}}`);
}

function documentationToString(value: unknown): string | undefined {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
        const maybe = value as { value?: unknown };
        if (typeof maybe.value === "string") return maybe.value;
    }
    return undefined;
}

export function markdownToHtml(markdown: string): string {
    const lines = normalizeHoverMarkdown(markdown).split("\n");
    const out: string[] = [];
    const body: string[] = [];
    let index = 0;
    let hasTitle = false;

    while (index < lines.length) {
        const line = lines[index];
        if (line.trim() === "") {
            index += 1;
            continue;
        }
        if (line.trim() === "---") {
            body.push('<hr class="mh-sep" />');
            index += 1;
            continue;
        }
        const table = parseMarkdownTable(lines, index);
        if (table) {
            body.push(table.html);
            index = table.nextIndex;
            continue;
        }
        if (line.startsWith("```")) {
            index += 1;
            const codeLines: string[] = [];
            while (index < lines.length && !lines[index].startsWith("```")) {
                codeLines.push(escapeHtml(lines[index]));
                index += 1;
            }
            if (index < lines.length) index += 1;
            body.push(`<pre class="mh-code"><code>${codeLines.join("\n")}</code></pre>`);
            continue;
        }
        if (/^- /.test(line)) {
            const items: string[] = [];
            while (index < lines.length && /^- /.test(lines[index])) {
                items.push(inlineMarkdownToHtml(lines[index].slice(2)));
                index += 1;
            }
            body.push(`<ul class="mh-list">${items.map((item) => `<li>${item}</li>`).join("")}</ul>`);
            continue;
        }

        if (!hasTitle) {
            out.push(`<div class="mh-title">${inlineMarkdownToHtml(line)}</div>`);
            hasTitle = true;
        } else {
            body.push(`<div class="mh-line">${inlineMarkdownToHtml(line)}</div>`);
        }
        index += 1;
    }

    if (body.length) {
        out.push(`<div class="mh-body">${body.join("")}</div>`);
    }

    return out.join("");
}

function normalizeHoverMarkdown(markdown: string): string {
    return markdown.replace(/&nbsp;|&#160;|&#xA0;/gi, " ");
}

function parseMarkdownTable(
    lines: string[],
    startIndex: number,
): { html: string; nextIndex: number } | null {
    const headerLine = lines[startIndex];
    const separatorLine = lines[startIndex + 1];
    if (!isMarkdownTableRow(headerLine) || !isMarkdownTableSeparator(separatorLine)) {
        return null;
    }

    const headers = splitMarkdownTableRow(headerLine);
    const alignments = splitMarkdownTableRow(separatorLine).map(tableCellAlignment);
    if (headers.length < 2) return null;

    const rows: string[][] = [];
    let index = startIndex + 2;
    while (index < lines.length && isMarkdownTableRow(lines[index]) && !isMarkdownTableSeparator(lines[index])) {
        rows.push(splitMarkdownTableRow(lines[index]));
        index += 1;
    }

    const renderCell = (cell: string, cellIndex: number, tag: "th" | "td") => {
        const align = alignments[cellIndex];
        const className = align && align !== "left" ? ` class="mh-align-${align}"` : "";
        return `<${tag}${className}>${inlineMarkdownToHtml(cell)}</${tag}>`;
    };
    const head = `<thead><tr>${headers.map((cell, cellIndex) => renderCell(cell, cellIndex, "th")).join("")}</tr></thead>`;
    const bodyRows = rows.map((row) => {
        const cells = headers.map((_, cellIndex) => renderCell(row[cellIndex] ?? "", cellIndex, "td"));
        return `<tr>${cells.join("")}</tr>`;
    });
    const body = bodyRows.length ? `<tbody>${bodyRows.join("")}</tbody>` : "";

    return {
        html: `<div class="mh-table-wrap"><table class="mh-table">${head}${body}</table></div>`,
        nextIndex: index,
    };
}

function isMarkdownTableRow(line: string | undefined): line is string {
    if (!line) return false;
    const trimmed = line.trim();
    return trimmed.includes("|") && splitMarkdownTableRow(trimmed).length >= 2;
}

function isMarkdownTableSeparator(line: string | undefined): boolean {
    if (!line) return false;
    const cells = splitMarkdownTableRow(line);
    return cells.length >= 2 && cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s+/g, "")));
}

function splitMarkdownTableRow(row: string): string[] {
    const trimmed = row.trim();
    const content = trimmed
        .replace(/^\|/, "")
        .replace(/\|$/, "");
    const cells: string[] = [];
    let current = "";

    for (let index = 0; index < content.length; index += 1) {
        const char = content[index];
        if (char === "\\" && index + 1 < content.length) {
            current += content[index + 1];
            index += 1;
            continue;
        }
        if (char === "|") {
            cells.push(cleanMarkdownTableCell(current));
            current = "";
            continue;
        }
        current += char;
    }
    cells.push(cleanMarkdownTableCell(current));
    return cells;
}

function cleanMarkdownTableCell(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}

function tableCellAlignment(cell: string): "left" | "center" | "right" | null {
    const normalized = cell.replace(/\s+/g, "");
    if (/^:-{3,}:$/.test(normalized)) return "center";
    if (/^-{3,}:$/.test(normalized)) return "right";
    if (/^:-{3,}$/.test(normalized)) return "left";
    return null;
}

function inlineMarkdownToHtml(value: string): string {
    return escapeHtml(value)
        .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
        .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, "<i>$1</i>")
        .replace(/`([^`]+?)`/g, "<code>$1</code>");
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

export function hoverContentsToString(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (Array.isArray(value)) return value.map(hoverContentsToString).filter(Boolean).join("\n\n") || null;
    if (typeof value === "object") {
        const object = value as { value?: unknown };
        if (typeof object.value === "string") return object.value;
    }
    return null;
}

function toCodeMirrorDiagnostic(
    doc: Text,
    diagnostic: LspDiagnostic,
    options?: {
        quickFix?: (view: EditorView, from: number, to: number) => void;
    },
): Diagnostic {
    const start = diagnostic.range?.start;
    const end = diagnostic.range?.end;
    const from = positionToOffset(doc, start?.line ?? 0, start?.character ?? 0);
    return {
        from,
        to: Math.max(from, positionToOffset(doc, end?.line ?? start?.line ?? 0, end?.character ?? start?.character ?? 0)),
        severity: severityToCodeMirror(diagnostic.severity),
        message: diagnostic.message ?? "LSP diagnostic",
        source: diagnostic.source,
        ...(options?.quickFix
            ? {
                actions: [{
                    name: pluginSdkT("lsp.quickFix"),
                    apply: options.quickFix,
                }],
            }
            : {}),
    };
}

function positionToOffset(doc: Text, line: number, character: number): number {
    const safeLine = Math.min(Math.max(line + 1, 1), doc.lines);
    const cmLine = doc.line(safeLine);
    return Math.min(cmLine.to, cmLine.from + Math.max(0, character));
}

function offsetToPosition(doc: Text, offset: number): { line: number; character: number } {
    const safeOffset = Math.min(Math.max(offset, 0), doc.length);
    const line = doc.lineAt(safeOffset);
    return {
        line: line.number - 1,
        character: safeOffset - line.from,
    };
}

function severityToCodeMirror(severity: number | undefined): Diagnostic["severity"] {
    if (severity === 1) return "error";
    if (severity === 2) return "warning";
    if (severity === 3) return "info";
    return "info";
}

function defaultServerIdForLanguage(languageId: string): string {
    if (languageId === "sql") return "postgres";
    if (
        languageId === "typescript" ||
        languageId === "tsx" ||
        languageId === "typescriptreact" ||
        languageId === "javascript" ||
        languageId === "jsx" ||
        languageId === "javascriptreact"
    ) {
        return "typescript";
    }
    return languageId;
}
